import React from 'react'
import * as Sentry from '@sentry/nextjs'
import { NextSeo } from 'next-seo'
import { config } from 'data/config'
import BannerCardSection from 'components/sections/BannerCardSection'
import PublicLayout from 'layouts/PublicLayout'
import { ArrowRightIcon } from 'components/icons'
import errorBanner from 'assets/images/404.png' // Using 404 image as fallback

class ErrorBoundary extends React.Component {
  constructor (props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError (_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true }
  }

  componentDidCatch (error, errorInfo) {
    // Log the error to Sentry
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', true)
      scope.setLevel('error')
      scope.setContext('errorBoundary', {
        componentStack: errorInfo.componentStack,
      })
      Sentry.captureException(error)
    })

    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }
  }

  render () {
    if (this.state.hasError) {
      // Professional error page that matches the 404 page structure
      return (
        <PublicLayout
          mobileNavBar={{
            visible: true,
            back: true,
            profileTrigger: true,
          }}
        >
          <NextSeo
            title="Something went wrong | AMA Selections"
            description="An unexpected error has occurred. Our team has been notified."
            openGraph={{
              type: 'website',
              url: `${config.appURL}/error`,
              images: [
                {
                  url: errorBanner?.src,
                  width: 1922,
                  height: 760,
                },
              ],
            }}
          />

          <BannerCardSection
            includePadding={false}
            className={{
              container: 'mt-80 sm:mt-100 lg:mt-0 mb-50 lg:mb-0',
            }}
            bannerImage={errorBanner}
            altText="Something went wrong"
            subtitle="Application Error"
            heading="Something went wrong"
            content={'We apologize for the inconvenience. An unexpected error has occurred and our team has been automatically notified. \n\nIf this problem persists, please contact our support team.'}
            buttons={[
              {
                text: 'Back to homepage',
                href: '/',
                icon: <ArrowRightIcon />,
              },
            ]}
            smShowContent
          />
        </PublicLayout>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
